import { cx } from '@emotion/css';
import { Divider, Tooltip, useMediaQuery } from '@sortlist-frontend/design-system/legacy';
import { ImageSourceHelper } from '@sortlist-frontend/media';
import { TFunction, useTranslation } from '@sortlist-frontend/translation/ssr';
import Image from 'next/image';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { usePublicAppContext } from '_core/context/public-app-context';
import { missingAgencyLogo, onImageError } from '_core/media/get-media.utils';
import { SectionBox } from '_features/AgencyProfile/components/SectionBox';
import { agencyProfileConfig } from '_features/AgencyProfile/config';
import { useGetAgencyProfile } from '_features/AgencyProfile/hooks/useGetAgencyProfile';

import * as S from './styles';
import { COVER_QUALITY, coverLoader, getCoverSource } from './utils';

type Props = {
  sections?: {
    id: string;
    label: string;
    icon: ReactNode;
  }[];
  children?: never;
};

export const HeroPanel = (props: Props) => {
  const { sections } = props;

  const { locale, pageData } = usePublicAppContext();
  const { agencySlug } = pageData as { agencySlug: string };
  const { data: { agency } = {} } = useGetAgencyProfile({ agencySlug, locale: locale as string });

  const agencyName = agency?.name;
  const coverSource = getCoverSource(agency?.coverPictureUrl);
  const agencyLogoUrl =
    agency?.logoUrl != null
      ? ImageSourceHelper.fromUrl(agency.logoUrl, { width: 150, quality: 75, format: 'auto' })
      : missingAgencyLogo;

  const [scroll, setScroll] = useState<number>(0);
  const [isStickyBar, setStickyBar] = useState<boolean>(false);

  const { media_legacy } = useMediaQuery();
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const anchorBar = useRef<HTMLDivElement>(null);

  const handleStickyAnchorbar = () => {
    if (anchorBar && anchorBar.current) {
      setScroll(anchorBar.current.getBoundingClientRect().top);
    }
    if (!media_legacy.xs && anchorBar && anchorBar.current && anchorBar.current.getBoundingClientRect().top <= 80) {
      setStickyBar(true);
    } else setStickyBar(false);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleStickyAnchorbar, true);
    return () => {
      window.removeEventListener('scroll', handleStickyAnchorbar, true);
    };
  }, [scroll]);

  const labels = getSectionTitleLabels(t);
  return (
    <SectionBox className={'display-inline relative'}>
      <S.HeroSectionHeader
        className={
          'overflow-hidden rounded-top-left-xl border-bottom border-secondary-200 rounded-top-right-xl width-100 bg-secondary-50 relative'
        }>
        <Image
          src={coverSource}
          loader={coverLoader}
          quality={COVER_QUALITY}
          sizes={'(max-width: 1120px) 100vw, 1120px'}
          alt={`${agencyName} cover`}
          onError={onImageError}
          fill
          priority
        />
      </S.HeroSectionHeader>
      <S.HeroSectionAnchorBar
        ref={anchorBar}
        className={`${isStickyBar && 'shadow-2 bg-neutral-50 sticky'} layout-row layout-align-center-center px-0`}>
        <div className="lg:px-16 layout-gt-sm-row flex-1 layout-column layout-align-gt-sm-center-center layout-align-center-stretch relative">
          {!(isStickyBar && media_legacy.gtSm) ? (
            <S.HeroSectionLogo
              width={112}
              height={112}
              src={agencyLogoUrl}
              alt={`${agencyName} logo`}
              className="bg-neutral-50 rounded-md shadow-2"
              unoptimized
            />
          ) : null}
          <div className="layout-column flex-1 hide-xs">
            <div className="layout-row overflow-y-auto">
              {sections &&
                sections.map((section, index) => (
                  <div
                    style={{ maxWidth: `${media_legacy.gtSm ? 100 / sections.length : 100}%` }}
                    className="layout-row flex-noshrink"
                    key={section.id}>
                    <Tooltip title={labels[section.id as keyof typeof labels]}>
                      <a
                        className={cx(
                          { 'border-right-secondary-500': index + 1 != sections.length },
                          'small cursor-pointer flex-1 layout-row layout-align-center-center btn btn-md btn-secondary btn-default',
                        )}
                        href={`#${section.id}`}>
                        <div className="mr-2 layout">{section.icon}</div>
                        <span className="text-truncate">{labels[section.id as keyof typeof labels]}</span>
                      </a>
                    </Tooltip>
                    <Divider className={cx({ hide: index + 1 === sections.length }, 'width-100 my-2')} />
                  </div>
                ))}
            </div>
            {!isStickyBar && <Divider />}
          </div>
        </div>
      </S.HeroSectionAnchorBar>
    </SectionBox>
  );
};

const getSectionTitleLabels = (t: TFunction) => {
  return {
    about: t('agency:profile.about.title'),
    awards: t('agency:profile.awards.title'),
    contact: t('agency:profile.contact.title'),
    faq: t('agency:profile.faq.title'),
    portfolio: t('agency:profile.portfolio.title'),
    reviews: t('agency:profile.reviews.title'),
    services: t('agency:profile.services.title'),
    similar: t('agency:profile.similar.title'),
    team: t('agency:profile.team.title'),
  };
};
